import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { LayoutChangeEvent, Platform, ScrollView, Text, View } from 'react-native';
import ErrorState from '../ErrorState';
import SpotLegend, { DEFAULT_SPOT_ITEMS, PREVIEW_SPOT_ITEMS } from '../SpotLegend';
import type { SpotLegendSpotItem } from '../SpotLegend';
import { FriendSpotSlots } from './FriendSpotSlots';
import {
  areSpotNameSetsEqual,
  buildFriendSlotChips,
  clearBlockedFriendSelections,
  clearFriendSlotsForError,
  createFriendSpotSelectionState,
  findFriendSlotsForSpotNames,
  getSelectedFriendSpotKeys,
  pressSpotForFriendSelection,
  resolveFriendSelectionSpots,
  type FriendSlotStrings,
  type FriendSpotSelectionState
} from './friendSpotSelection';
import RoomLayoutGrid from './RoomLayoutGrid';
import SpotGridZoomViewportLazy from './SpotGridZoomViewportLazy';
import { SPOT_GRID_DEFAULT_ZOOM, SPOT_GRID_MIN_ZOOM } from './spotGridZoomConstants';
import { getZoomViewportHeight } from './spotGridZoomBounds';
import {
  getFixedViewportLegendMaxHeight,
  getPinchZoomMaxSlotHeight
} from './spotGridLegendReserve';
import type { RoomLayoutMapProps } from './RoomLayoutMap.types';
import type { RoomLayoutApiResponse, RoomLayoutError, RoomLayoutSpot } from './RoomLayoutMap.types';
import { RoomLayoutErrorCode, SpotStatus } from './RoomLayoutMap.types';
import { RoomLayoutMapStrings } from './RoomLayoutMap.strings';
import { styles } from './RoomLayoutMap.styles';
import { getScrollableGridContentDimensions } from './RoomLayoutGrid.styles';
import { enrichSpotsWithLayoutSpotTypePrefix } from './spotTypeEnrichment';
import {
  deriveSpotTypeLegendItemsFromSpots,
  spotTypeLegendItemsFromSpotTypesForBookableSpots
} from './spotTypeLegend';
import { applySpotsOverlayForMap } from './applySpotsOverlayForMap';
import { computeReservedKeysForMap } from './computeReservedKeysForMap';
import { countBookableSpots } from './computeBookableSpotsForMap';
import { resolveSpotKeyInLayout } from './spotKey';
import { isSelectionReservedByOthers } from './spotSelectionCleanup';
import { useRoomLayoutMap } from './useRoomLayoutMap';
import { useScrollToCenter } from './useScrollToCenter';

function errorToTitleMessage(error: RoomLayoutError): { title: string; message: string } {
  const {
    ERROR_FALLBACK_MESSAGE,
    ERROR_TITLE_LAYOUT_UNAVAILABLE,
    ERROR_MESSAGE_NOT_FOUND,
    ERROR_MESSAGE_LAYOUT_INACTIVE,
    ERROR_TITLE_NETWORK,
    ERROR_MESSAGE_CHECK_CONNECTION,
    ERROR_TITLE_SOMETHING_WRONG
  } = RoomLayoutMapStrings;
  switch (error.code) {
    case RoomLayoutErrorCode.NOT_FOUND:
      return { title: ERROR_TITLE_LAYOUT_UNAVAILABLE, message: ERROR_MESSAGE_NOT_FOUND };
    case RoomLayoutErrorCode.LAYOUT_INACTIVE:
      return {
        title: ERROR_TITLE_LAYOUT_UNAVAILABLE,
        message: ERROR_MESSAGE_LAYOUT_INACTIVE
      };
    // Timeouts, no internet, and fetch failures: always use connection message, never 404 or error.message.
    case RoomLayoutErrorCode.CONNECTION_ERROR:
      return {
        title: ERROR_TITLE_NETWORK,
        message: ERROR_MESSAGE_CHECK_CONNECTION
      };
    case RoomLayoutErrorCode.CLIENT_ERROR:
    case RoomLayoutErrorCode.SERVER_ERROR:
    case RoomLayoutErrorCode.UNKNOWN:
    default:
      return {
        title: ERROR_TITLE_SOMETHING_WRONG,
        message: error.message ?? ERROR_FALLBACK_MESSAGE
      };
  }
}

/** Phase 1: returns layout as JSON; interactive UI in later phases. Spots with status BOOKABLE are selectable. */
const LOADING_SKIP_THRESHOLD_MS = 10_000;

/** English fallbacks for the friends-mode chip labels when the host passes no localized strings. */
const DEFAULT_FRIEND_SLOT_STRINGS: FriendSlotStrings = {
  YOUR_SPOT: RoomLayoutMapStrings.YOUR_SPOT,
  FRIEND_SLOT: RoomLayoutMapStrings.FRIEND_SLOT
};

/** Friends mode legend: the SpotLegend defaults with only "Selected spot" swapped for the plural. */
function buildFriendsLegendSpotItems(selectedSpotsLabel: string): SpotLegendSpotItem[] {
  return DEFAULT_SPOT_ITEMS.map((item) =>
    item.key === 'selected' ? { ...item, label: selectedSpotsLabel } : item
  );
}

export default function RoomLayoutMap({
  envUrl,
  layoutId,
  userToken,
  timeSlotId,
  reservedSpotKeys,
  onSuccess,
  onError,
  onSpotSelect,
  onSkipLoadingAvailable,
  onErrorStateChange,
  onRefetchReady,
  onBookableAvailabilityChange,
  renderLoading,
  selectedSpotKey: controlledSelectedSpotKey,
  assignedSpotKey: controlledAssignedSpotKey,
  selectedSpotColor,
  loadingMinimumMs,
  pinchToZoomEnabled = false,
  pinchToZoomOutEnabled = false,
  pinchZoomSlotHeight,
  hideLegend = false,
  pinchEmbedInPageScroll = false,
  useCustomNonBookablePrefixes = false,
  isSpotSelectionForFriendsEnabled = false,
  guestsCount = 0,
  failedReserveSpotKeys,
  friendSlotStrings = DEFAULT_FRIEND_SLOT_STRINGS,
  selectedSpotsLegendLabel = RoomLayoutMapStrings.LEGEND_SELECTED_SPOTS,
  onFriendsSpotSelectionChange,
  onFriendSpotPress,
  selectionDisabled = false
}: RoomLayoutMapProps) {
  const [internalSelectedSpotKey, setInternalSelectedSpotKey] = useState<string | null>(null);
  const [friendSelection, setFriendSelection] = useState(() =>
    createFriendSpotSelectionState(guestsCount)
  );
  const friendSelectionResetKeyRef = useRef<string | null>(`${String(guestsCount)}|${layoutId}`);
  const [erroredSlotIndexes, setErroredSlotIndexes] = useState<number[]>([]);
  const appliedFailedSpotKeysRef = useRef<string[]>([]);

  const isControlledSelection = controlledSelectedSpotKey !== undefined;

  const selectedSpotKey = isControlledSelection
    ? (controlledSelectedSpotKey ?? null)
    : internalSelectedSpotKey;

  const assignedSpotKey = controlledAssignedSpotKey ?? null;

  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const callbacks = useMemo(
    () => ({
      onSuccess: (response: RoomLayoutApiResponse) => onSuccessRef.current?.(response),
      onError: (err: RoomLayoutError) => onErrorRef.current?.(err),
      ...(loadingMinimumMs !== undefined && { loadingMinimumMs })
    }),
    [loadingMinimumMs]
  );

  const { data, isLoading, isError, error, refetch, isErrorRef } = useRoomLayoutMap(
    layoutId,
    envUrl,
    userToken,
    callbacks,
    timeSlotId !== undefined && timeSlotId !== '' ? timeSlotId : undefined
  );

  const onSkipLoadingAvailableRef = useRef(onSkipLoadingAvailable);
  onSkipLoadingAvailableRef.current = onSkipLoadingAvailable;
  const onErrorStateChangeRef = useRef(onErrorStateChange);
  onErrorStateChangeRef.current = onErrorStateChange;
  const onRefetchReadyRef = useRef(onRefetchReady);
  onRefetchReadyRef.current = onRefetchReady;
  const onBookableAvailabilityChangeRef = useRef(onBookableAvailabilityChange);
  onBookableAvailabilityChangeRef.current = onBookableAvailabilityChange;
  const onSpotSelectRef = useRef(onSpotSelect);
  onSpotSelectRef.current = onSpotSelect;
  const onFriendsSpotSelectionChangeRef = useRef(onFriendsSpotSelectionChange);
  onFriendsSpotSelectionChangeRef.current = onFriendsSpotSelectionChange;
  const onFriendSpotPressRef = useRef(onFriendSpotPress);
  onFriendSpotPressRef.current = onFriendSpotPress;
  // Read by onSpotPress at tap time; synced from cleanedFriendSelection below so it never lags a render.
  const friendSelectionRef = useRef<FriendSpotSelectionState | null>(null);

  useEffect(() => {
    if (!isLoading) {
      onSkipLoadingAvailableRef.current?.(false);
      return;
    }
    const timer = setTimeout(() => {
      if (!isErrorRef.current) {
        onSkipLoadingAvailableRef.current?.(true);
      }
    }, LOADING_SKIP_THRESHOLD_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [isLoading, isErrorRef]);

  useEffect(() => {
    onErrorStateChangeRef.current?.(isError, error ?? null);
  }, [isError, error]);

  useEffect(() => {
    onRefetchReadyRef.current?.(refetch);
  }, [refetch]);

  const {
    scrollRef: horizontalScrollRef,
    onLayout: onHorizontalScrollLayout,
    onContentSizeChange: onHorizontalScrollContentSizeChange,
    reset: resetHorizontalScroll
  } = useScrollToCenter();
  const [gridContentWidth, setGridContentWidth] = useState(0);
  const [gridContentHeight, setGridContentHeight] = useState(0);
  const gridMetricsLayoutIdRef = useRef(layoutId);
  const layoutIdRef = useRef(layoutId);
  layoutIdRef.current = layoutId;
  const [pinchOverlayHeight, setPinchOverlayHeight] = useState(0);
  const [legendHeight, setLegendHeight] = useState(0);
  const [friendSlotsHeight, setFriendSlotsHeight] = useState(0);
  const gridMetricsMatchLayout = gridMetricsLayoutIdRef.current === layoutId;
  const modeledGridContentDimensions = useMemo(
    () => getScrollableGridContentDimensions(data?.columns ?? 0, data?.rows ?? 0),
    [data?.columns, data?.rows]
  );
  const effectiveGridContentWidth = gridMetricsMatchLayout
    ? gridContentWidth || modeledGridContentDimensions.width
    : 0;
  const effectiveGridContentHeight = gridMetricsMatchLayout
    ? gridContentHeight || modeledGridContentDimensions.height
    : 0;
  const hasExternalPinchSlotHeight = pinchZoomSlotHeight != null && pinchZoomSlotHeight > 0;
  const shouldMeasurePinchOverlay =
    pinchToZoomEnabled && (!pinchEmbedInPageScroll || !hasExternalPinchSlotHeight);

  // Friends mode: slots restart empty whenever the layout or party size changes. The applied-error
  // ref is deliberately not reset: leaving it latched stops a stale-but-still-passed
  // failedReserveSpotKeys from re-applying against the fresh empty state.
  useLayoutEffect(() => {
    if (!isSpotSelectionForFriendsEnabled) {
      friendSelectionResetKeyRef.current = null;
      return;
    }
    const resetKey = `${String(guestsCount)}|${layoutId}`;
    if (friendSelectionResetKeyRef.current === resetKey) return;
    friendSelectionResetKeyRef.current = resetKey;
    setFriendSelection(createFriendSpotSelectionState(guestsCount));
    setErroredSlotIndexes((prev) => (prev.length === 0 ? prev : []));
  }, [isSpotSelectionForFriendsEnabled, guestsCount, layoutId]);

  useLayoutEffect(() => {
    resetHorizontalScroll();
    if (gridMetricsLayoutIdRef.current !== layoutId) {
      gridMetricsLayoutIdRef.current = layoutId;
      setGridContentWidth(0);
      setGridContentHeight(0);
      setLegendHeight(0);
      setPinchOverlayHeight(0);
    }
  }, [layoutId, resetHorizontalScroll]);

  useLayoutEffect(() => {
    if (pinchToZoomEnabled) return;
    resetHorizontalScroll();
  }, [pinchToZoomEnabled, resetHorizontalScroll]);

  const onPinchOverlayLayout = useCallback(
    (e: LayoutChangeEvent) => {
      if (layoutId !== layoutIdRef.current) return;
      setPinchOverlayHeight(e.nativeEvent.layout.height);
    },
    [layoutId]
  );

  const onLegendLayout = useCallback(
    (e: LayoutChangeEvent) => {
      if (layoutId !== layoutIdRef.current) return;
      setLegendHeight(e.nativeEvent.layout.height);
    },
    [layoutId]
  );

  const onFriendSlotsLayout = useCallback(
    (e: LayoutChangeEvent) => {
      if (layoutId !== layoutIdRef.current) return;
      setFriendSlotsHeight(e.nativeEvent.layout.height);
    },
    [layoutId]
  );

  const pinchOverlayHeightForSlot =
    pinchZoomSlotHeight != null && pinchZoomSlotHeight > 0
      ? pinchZoomSlotHeight
      : pinchOverlayHeight;

  const effectiveLegendHeight = hideLegend ? 0 : legendHeight;

  const fixedViewportLegendMaxHeight = useMemo(() => {
    if (!pinchToZoomEnabled || pinchEmbedInPageScroll || hideLegend) return undefined;
    return getFixedViewportLegendMaxHeight(pinchOverlayHeightForSlot);
  }, [pinchToZoomEnabled, pinchEmbedInPageScroll, hideLegend, pinchOverlayHeightForSlot]);

  // The chips row shares the overlay with the grid, so it only counts while friends mode renders it.
  const effectiveFriendSlotsHeight = isSpotSelectionForFriendsEnabled ? friendSlotsHeight : 0;

  const pinchZoomMaxSlotHeight = useMemo(
    () =>
      getPinchZoomMaxSlotHeight(
        pinchOverlayHeightForSlot,
        effectiveLegendHeight,
        hideLegend,
        effectiveFriendSlotsHeight,
        fixedViewportLegendMaxHeight
      ),
    [
      pinchOverlayHeightForSlot,
      effectiveLegendHeight,
      hideLegend,
      effectiveFriendSlotsHeight,
      fixedViewportLegendMaxHeight
    ]
  );

  const zoomViewportHeight = useMemo(
    () =>
      getZoomViewportHeight(
        effectiveGridContentHeight,
        pinchZoomMaxSlotHeight,
        pinchEmbedInPageScroll
      ),
    [effectiveGridContentHeight, pinchZoomMaxSlotHeight, pinchEmbedInPageScroll]
  );

  const shouldClampPinchViewport =
    pinchToZoomEnabled &&
    effectiveGridContentHeight > 0 &&
    zoomViewportHeight != null &&
    zoomViewportHeight > 0;

  const onGridContentLayout = useCallback(
    (e: LayoutChangeEvent) => {
      if (layoutId !== layoutIdRef.current) return;
      const { width, height } = e.nativeEvent.layout;
      if (width <= 0 || height <= 0) return;
      gridMetricsLayoutIdRef.current = layoutId;
      setGridContentWidth(width);
      setGridContentHeight(height);
    },
    [layoutId]
  );

  const onSpotPress = useCallback(
    (spot: RoomLayoutSpot) => {
      if (selectionDisabled) return;
      if (spot.status !== SpotStatus.BOOKABLE) return;
      if (isSpotSelectionForFriendsEnabled) {
        // A tap on an assigned spot deselects its owning slot (which may not be the active one),
        // so the callback must report the owner; the active slot only receives assignments.
        const selection = friendSelectionRef.current;
        const ownerIndex = selection?.selections.indexOf(spot.spotName) ?? -1;
        onFriendSpotPressRef.current?.(
          spot,
          ownerIndex !== -1 ? ownerIndex : (selection?.activeSlotIndex ?? 0),
          ownerIndex !== -1 ? 'unselect' : 'select'
        );
        setFriendSelection((prev) => pressSpotForFriendSelection(prev, spot.spotName));
        return;
      }
      if (!isControlledSelection) {
        setInternalSelectedSpotKey(spot.spotName);
      }
      onSpotSelectRef.current?.(spot);
    },
    [isControlledSelection, isSpotSelectionForFriendsEnabled, selectionDisabled]
  );

  const layoutSpots = useMemo(
    () => enrichSpotsWithLayoutSpotTypePrefix(data?.spots ?? [], data?.spot_types),
    [data?.spot_types, data?.spots]
  );

  const spotTypeItems = useMemo(() => {
    if (!layoutSpots.length) {
      if (data?.spot_types?.length) {
        return spotTypeLegendItemsFromSpotTypesForBookableSpots(data.spot_types, layoutSpots);
      }
      return undefined;
    }
    const derived = deriveSpotTypeLegendItemsFromSpots(layoutSpots);
    if (derived !== undefined) return derived;
    if (data?.spot_types?.length)
      return spotTypeLegendItemsFromSpotTypesForBookableSpots(data.spot_types, layoutSpots);
    return undefined;
  }, [data?.spot_types, layoutSpots]);

  const resolvedAssignedSpotKey = useMemo(
    () => resolveSpotKeyInLayout(layoutSpots, assignedSpotKey),
    [layoutSpots, assignedSpotKey]
  );

  const resolvedSelectedSpotKey = useMemo(
    () => resolveSpotKeyInLayout(layoutSpots, selectedSpotKey) ?? selectedSpotKey,
    [layoutSpots, selectedSpotKey]
  );

  const hasAssignedSpot = resolvedAssignedSpotKey !== null;

  // A spot the reserve API rejected is gone for this member, so it blocks for as long as the prop
  // holds it — level-triggered, unlike the edge-triggered chip reddening below. The prop must be
  // reference-stable (documented in RoomLayoutMapProps): a fresh non-empty array per render would
  // cascade new references into spotsToShow and fire onFriendsSpotSelectionChange every render.
  const blockedSpotKeys = useMemo(() => {
    if (!isSpotSelectionForFriendsEnabled || !failedReserveSpotKeys?.length)
      return reservedSpotKeys;
    return computeReservedKeysForMap(reservedSpotKeys ?? [], failedReserveSpotKeys);
  }, [isSpotSelectionForFriendsEnabled, reservedSpotKeys, failedReserveSpotKeys]);

  const spotsToShow = useMemo(
    (): RoomLayoutSpot[] =>
      applySpotsOverlayForMap(layoutSpots, blockedSpotKeys, resolvedAssignedSpotKey),
    [layoutSpots, blockedSpotKeys, resolvedAssignedSpotKey]
  );

  const bookableAvailability = useMemo(() => countBookableSpots(spotsToShow), [spotsToShow]);
  const { bookableCount, hasBookableSpots } = bookableAvailability;

  useEffect(() => {
    if (isLoading || isError || !data) return;
    onBookableAvailabilityChangeRef.current?.({ bookableCount, hasBookableSpots });
  }, [isLoading, isError, data, bookableCount, hasBookableSpots]);

  // In uncontrolled mode, clear selection when the selected spot becomes BLOCKED (e.g. reserved by someone else).
  // Check reserved keys directly so we clear even when the parent passes a new array reference without spotsToShow updating first.
  useEffect(() => {
    if (isControlledSelection) return;
    const key = internalSelectedSpotKey;
    if (key === null) return;
    const selectedSpot = spotsToShow.find((s) => s.spotName === key);
    if (isSelectionReservedByOthers(selectedSpot, key, reservedSpotKeys, resolvedAssignedSpotKey)) {
      setInternalSelectedSpotKey(null);
      return;
    }
    if (selectedSpot?.status === SpotStatus.BLOCKED) {
      setInternalSelectedSpotKey(null);
    }
  }, [
    spotsToShow,
    reservedSpotKeys,
    internalSelectedSpotKey,
    isControlledSelection,
    resolvedAssignedSpotKey
  ]);

  // Friends mode: drop any slot whose spot became BLOCKED / reserved (multi-slot version of the
  // effect above). Derived during render, not in an effect, so the host callback below never
  // reports a spot that the cleanup is about to clear. clearBlockedFriendSelections returns the
  // same reference when nothing changed, so the sync effect settles after one pass.
  const cleanedFriendSelection = useMemo(
    () =>
      isSpotSelectionForFriendsEnabled
        ? clearBlockedFriendSelections(
            friendSelection,
            spotsToShow,
            reservedSpotKeys,
            resolvedAssignedSpotKey
          )
        : friendSelection,
    [
      isSpotSelectionForFriendsEnabled,
      friendSelection,
      spotsToShow,
      reservedSpotKeys,
      resolvedAssignedSpotKey
    ]
  );
  friendSelectionRef.current = cleanedFriendSelection;
  useEffect(() => {
    if (cleanedFriendSelection === friendSelection) return;
    setFriendSelection((prev) =>
      clearBlockedFriendSelections(prev, spotsToShow, reservedSpotKeys, resolvedAssignedSpotKey)
    );
  }, [
    cleanedFriendSelection,
    friendSelection,
    spotsToShow,
    reservedSpotKeys,
    resolvedAssignedSpotKey
  ]);

  useEffect(() => {
    if (!isSpotSelectionForFriendsEnabled) return;
    const failedKeys = failedReserveSpotKeys ?? [];
    if (areSpotNameSetsEqual(appliedFailedSpotKeysRef.current, failedKeys)) return;
    appliedFailedSpotKeysRef.current = [...failedKeys];
    // Resolved against the raw selection, not cleanedFriendSelection: blockedSpotKeys already
    // rendered these spots BLOCKED in this same commit, so the cleanup has emptied those slots and
    // the keys would no longer map to an index. Reading the pre-cleanup selection is what makes the
    // slot end up errored (red) rather than merely empty.
    const targets = findFriendSlotsForSpotNames(friendSelection, spotsToShow, failedKeys);
    if (targets.length === 0) {
      setErroredSlotIndexes((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    setErroredSlotIndexes(targets);
    setFriendSelection((prev) => clearFriendSlotsForError(prev, targets));
  }, [isSpotSelectionForFriendsEnabled, failedReserveSpotKeys, friendSelection, spotsToShow]);

  // Friends mode: report every selection change (assign, deselect, auto-clear, reset) to the host.
  // Resolved against the current spotsToShow so a refetch that rewrites a still-selected spot also
  // re-reports it — otherwise the host would keep the spot object from the tap that selected it.
  const friendSelectionSpots = useMemo(
    () => resolveFriendSelectionSpots(cleanedFriendSelection, spotsToShow),
    [cleanedFriendSelection, spotsToShow]
  );
  useEffect(() => {
    if (!isSpotSelectionForFriendsEnabled) return;
    onFriendsSpotSelectionChangeRef.current?.(friendSelectionSpots);
  }, [isSpotSelectionForFriendsEnabled, friendSelectionSpots]);

  const friendSlotChips = useMemo(
    () =>
      isSpotSelectionForFriendsEnabled
        ? buildFriendSlotChips(cleanedFriendSelection, spotsToShow, friendSlotStrings)
        : null,
    [isSpotSelectionForFriendsEnabled, cleanedFriendSelection, spotsToShow, friendSlotStrings]
  );

  const friendsLegendSpotItems = useMemo(
    () => buildFriendsLegendSpotItems(selectedSpotsLegendLabel),
    [selectedSpotsLegendLabel]
  );

  const visibleErrorSlotIndexes = useMemo(
    () => erroredSlotIndexes.filter((index) => cleanedFriendSelection.selections[index] === null),
    [erroredSlotIndexes, cleanedFriendSelection]
  );
  // Write the filtered list back into state: once a slot is refilled its error is gone for good,
  // not just hidden. Without this, deselecting the re-picked spot would empty the slot again and
  // resurrect the already-cleared red chip.
  useEffect(() => {
    if (visibleErrorSlotIndexes.length === erroredSlotIndexes.length) return;
    setErroredSlotIndexes(visibleErrorSlotIndexes);
  }, [visibleErrorSlotIndexes, erroredSlotIndexes]);

  const friendSelectedSpotKeys = useMemo(
    () =>
      isSpotSelectionForFriendsEnabled
        ? getSelectedFriendSpotKeys(cleanedFriendSelection)
        : undefined,
    [isSpotSelectionForFriendsEnabled, cleanedFriendSelection]
  );

  const onFriendSlotPress = useCallback((index: number) => {
    setFriendSelection((prev) =>
      prev.activeSlotIndex === index ? prev : { ...prev, activeSlotIndex: index }
    );
  }, []);

  const layoutColumns = data?.columns ?? 0;
  const layoutRows = data?.rows ?? 0;

  const renderGrid = useCallback(
    () => (
      <RoomLayoutGrid
        columns={layoutColumns}
        rows={layoutRows}
        spots={spotsToShow}
        selectedSpotKey={friendSelectedSpotKeys !== undefined ? null : resolvedSelectedSpotKey}
        {...(friendSelectedSpotKeys !== undefined
          ? { selectedSpotKeys: friendSelectedSpotKeys }
          : {})}
        assignedSpotKey={resolvedAssignedSpotKey}
        {...(selectedSpotColor !== undefined ? { selectedSpotColor } : {})}
        {...(selectionDisabled ? {} : { onSpotPress })}
        scrollable
        useCustomNonBookablePrefixes={useCustomNonBookablePrefixes}
      />
    ),
    [
      layoutColumns,
      layoutRows,
      spotsToShow,
      resolvedSelectedSpotKey,
      friendSelectedSpotKeys,
      resolvedAssignedSpotKey,
      selectedSpotColor,
      onSpotPress,
      selectionDisabled,
      useCustomNonBookablePrefixes
    ]
  );

  const renderPinchGridContent = useCallback(
    () => (
      <View onLayout={onGridContentLayout} testID="room-layout-grid-pinch-content">
        {renderGrid()}
      </View>
    ),
    [onGridContentLayout, renderGrid]
  );

  /** Web/UMD: zoom viewport is a passthrough — keep legacy horizontal scroll for wide grids. */
  const renderGridInPinchZoom = useCallback(() => {
    const gridContent = renderPinchGridContent();
    if (Platform.OS !== 'web') {
      return gridContent;
    }

    return (
      <View onLayout={onHorizontalScrollLayout}>
        <ScrollView
          ref={horizontalScrollRef}
          horizontal
          nestedScrollEnabled
          style={styles.gridZoomHorizontalScroll}
          contentContainerStyle={styles.horizontalScrollContent}
          showsHorizontalScrollIndicator
          onContentSizeChange={onHorizontalScrollContentSizeChange}
          testID="room-layout-grid-horizontal-scroll-pinch"
        >
          {gridContent}
        </ScrollView>
      </View>
    );
  }, [
    renderPinchGridContent,
    onHorizontalScrollLayout,
    horizontalScrollRef,
    onHorizontalScrollContentSizeChange
  ]);

  return (
    <View
      style={[
        styles.container,
        pinchToZoomEnabled && !pinchEmbedInPageScroll && styles.containerPinchZoom
      ]}
      testID="room-layout-map"
    >
      {isLoading && !data && (
        <View testID="room-layout-map-loading">
          {renderLoading ? renderLoading() : <Text>{RoomLayoutMapStrings.LOADING_LAYOUT}</Text>}
        </View>
      )}

      {isError && error && (
        <ErrorState {...errorToTitleMessage(error)} testID="room-layout-map-error" />
      )}
      {!isError && data && (
        <View
          style={[
            styles.contentWithOverlay,
            shouldMeasurePinchOverlay &&
              !pinchEmbedInPageScroll &&
              styles.contentWithOverlayPinchZoom
          ]}
          onLayout={shouldMeasurePinchOverlay ? onPinchOverlayLayout : undefined}
        >
          {friendSlotChips !== null && (
            // Measured so the pinch zoom viewport reserves the chips row, same as the legend below.
            <View onLayout={pinchToZoomEnabled ? onFriendSlotsLayout : undefined}>
              <FriendSpotSlots
                slots={friendSlotChips}
                activeIndex={cleanedFriendSelection.activeSlotIndex}
                errorSlotIndexes={visibleErrorSlotIndexes}
                {...(selectedSpotColor !== undefined ? { selectedSpotColor } : {})}
                onSlotPress={onFriendSlotPress}
              />
            </View>
          )}
          {pinchToZoomEnabled ? (
            <View
              style={[styles.gridZoomSlot, pinchEmbedInPageScroll && styles.gridZoomSlotPageScroll]}
            >
              <View
                style={[
                  styles.gridScrollContainer,
                  !pinchEmbedInPageScroll &&
                    !shouldClampPinchViewport &&
                    styles.gridZoomViewportFill,
                  shouldClampPinchViewport && styles.gridZoomViewport,
                  shouldClampPinchViewport && { height: zoomViewportHeight }
                ]}
              >
                <SpotGridZoomViewportLazy
                  enabled
                  minimumScale={pinchToZoomOutEnabled ? SPOT_GRID_MIN_ZOOM : SPOT_GRID_DEFAULT_ZOOM}
                  resetKey={`${layoutId}:${timeSlotId ?? ''}`}
                  contentWidth={effectiveGridContentWidth}
                  contentHeight={effectiveGridContentHeight}
                  fallback={renderPinchGridContent()}
                  testID="room-layout-grid-zoom-viewport"
                >
                  {renderGridInPinchZoom()}
                </SpotGridZoomViewportLazy>
              </View>
            </View>
          ) : (
            <View style={styles.gridScrollContainer} onLayout={onHorizontalScrollLayout}>
              <ScrollView
                ref={horizontalScrollRef}
                horizontal
                contentContainerStyle={styles.horizontalScrollContent}
                showsHorizontalScrollIndicator={Platform.OS === 'web'}
                onContentSizeChange={onHorizontalScrollContentSizeChange}
                testID="room-layout-grid-horizontal-scroll"
              >
                {renderGrid()}
              </ScrollView>
            </View>
          )}
          {!hideLegend ? (
            <View
              style={styles.legendSpacer}
              onLayout={pinchToZoomEnabled ? onLegendLayout : undefined}
              testID="room-layout-legend-container"
            >
              <SpotLegend
                {...(selectionDisabled && !hasAssignedSpot
                  ? { spotItems: PREVIEW_SPOT_ITEMS }
                  : isSpotSelectionForFriendsEnabled
                    ? { spotItems: friendsLegendSpotItems }
                    : {})}
                {...(spotTypeItems !== undefined ? { spotTypeItems } : {})}
                {...(selectedSpotColor !== undefined ? { selectedSpotColor } : {})}
                {...(fixedViewportLegendMaxHeight !== undefined
                  ? { maxHeight: fixedViewportLegendMaxHeight }
                  : {})}
              />
            </View>
          ) : null}
        </View>
      )}
    </View>
  );
}
